import { MaterialTable, MatFlag, MatKind, Pattern, setLiquidMaterials, type MaterialDef } from '@vm/shared';

const K = MatKind;
const T = MatFlag.Transparent;
const E = MatFlag.Emissive;
const F = MatFlag.Flammable;
const TR = MatFlag.Terrain;

/** Explosion caused by destroying TNT voxels (per cluster). */
export const TNT_EXPLOSION = { radius: 3.8, damage: 95, voxelPower: 65, voxelRadius: 1.7, knockback: 12, selfDamage: 1 };

/**
 * Global world palette. Index 0 = air, 1 = bedrock, 255 is reserved for the mesher.
 * Order is part of the network/save format: append new entries, never reorder.
 * HP guide (a rifle bullet deals 4): glass/leaves 1, soft 2-4, wood 4-5, brick 6, stone/concrete 8,
 * metal 18, plate 38. Natural ground carries the Terrain flag (tough; explosions only crater its
 * core): snow/ice 12, grass/dirt/sand 16, roads 24, rock 32. Player-built blocks keep their own HP.
 */
const defs: [string, number, number, MatKind, number?][] = [
  // name, color, hp, kind, flags
  ['bedrock', 0x2b2a30, 0, K.Bedrock],
  ['grass_1', 0x5fa83a, 16, K.Dirt, TR],
  ['grass_2', 0x55a035, 16, K.Dirt, TR],
  ['grass_3', 0x6bb542, 16, K.Dirt, TR],
  ['grass_4', 0x4c9230, 16, K.Dirt, TR],
  ['dirt_1', 0x8a5a36, 16, K.Dirt, TR],
  ['dirt_2', 0x7d5131, 16, K.Dirt, TR],
  ['dirt_3', 0x94633c, 16, K.Dirt, TR],
  ['stone_1', 0x8c8f94, 8, K.Stone],
  ['stone_2', 0x7f8287, 8, K.Stone],
  ['stone_3', 0x999ca1, 8, K.Stone],
  ['stone_4', 0x6f7277, 8, K.Stone],
  ['cobble_1', 0x6d6e70, 8, K.Stone],
  ['cobble_2', 0x5d5e60, 8, K.Stone],
  ['sand_1', 0xe0c68a, 16, K.Sand, TR],
  ['sand_2', 0xd6bb7d, 16, K.Sand, TR],
  ['sand_3', 0xe8d09a, 16, K.Sand, TR],
  ['sandstone_1', 0xd9b47a, 6, K.Stone],
  ['sandstone_2', 0xc9a26a, 6, K.Stone],
  ['sandstone_3', 0xe3c28e, 6, K.Stone],
  ['snow_1', 0xf2f6fa, 12, K.Snow, TR],
  ['snow_2', 0xe2eaf2, 12, K.Snow, TR],
  ['ice', 0xa9d8f0, 12, K.Ice, T | TR],
  ['brick_red_1', 0xa8472f, 6, K.Brick],
  ['brick_red_2', 0x9a3f2a, 6, K.Brick],
  ['brick_red_3', 0xb45235, 6, K.Brick],
  ['brick_orange', 0xc4683a, 6, K.Brick],
  ['brick_yellow', 0xd9b25f, 6, K.Brick],
  ['brick_gray', 0x8a8580, 6, K.Brick],
  ['mortar', 0xc9c2b5, 6, K.Brick],
  ['concrete_1', 0xb5b5b0, 8, K.Concrete],
  ['concrete_2', 0xa8a8a3, 8, K.Concrete],
  ['concrete_3', 0xc2c2bd, 8, K.Concrete],
  ['concrete_4', 0x96968f, 8, K.Concrete],
  ['asphalt_1', 0x3c3d40, 24, K.Concrete, TR],
  ['asphalt_2', 0x353639, 24, K.Concrete, TR],
  ['road_white', 0xe8e0c0, 24, K.Concrete, TR],
  ['road_yellow', 0xe0b43a, 24, K.Concrete, TR],
  ['planks_1', 0xb08050, 4, K.Wood, F],
  ['planks_2', 0xa3744a, 4, K.Wood, F],
  ['planks_3', 0xbd8d5a, 4, K.Wood, F],
  ['planks_4', 0x8f6440, 4, K.Wood, F],
  ['planks_dark_1', 0x6b4a30, 4, K.Wood, F],
  ['planks_dark_2', 0x5e412a, 4, K.Wood, F],
  ['log_1', 0x7a5535, 5, K.Wood, F],
  ['log_2', 0x6b4a2e, 5, K.Wood, F],
  ['leaves_1', 0x3f8a3a, 1, K.Leaves, F],
  ['leaves_2', 0x4a9a42, 1, K.Leaves, F],
  ['leaves_3', 0x357a30, 1, K.Leaves, F],
  ['leaves_4', 0x5aa84a, 1, K.Leaves, F],
  ['leaves_autumn_1', 0xd9822b, 1, K.Leaves, F],
  ['leaves_autumn_2', 0xc4502a, 1, K.Leaves, F],
  ['metal_1', 0x9aa3ad, 18, K.Metal],
  ['metal_2', 0x8a939d, 18, K.Metal],
  ['metal_3', 0xaab3bd, 18, K.Metal],
  ['steel_dark_1', 0x4a5058, 18, K.Metal],
  ['steel_dark_2', 0x3e444b, 18, K.Metal],
  ['rust', 0x9a5a35, 15, K.Metal],
  ['plate', 0x5c6570, 38, K.Metal],
  ['glass', 0xc4e8ff, 1, K.Glass, T],
  ['glass_tint', 0x8fc4e0, 1, K.Glass, T],
  ['marble_1', 0xece8e0, 8, K.Stone],
  ['marble_2', 0xdcd6cc, 8, K.Stone],
  ['gold', 0xe8b830, 18, K.Metal],
  ['roof_red_1', 0xb3402e, 5, K.Brick],
  ['roof_red_2', 0xa33a2a, 5, K.Brick],
  ['roof_blue', 0x3f6fa8, 5, K.Brick],
  ['roof_slate', 0x4f5563, 5, K.Stone],
  ['plaster_1', 0xeee3cf, 5, K.Concrete],
  ['plaster_2', 0xe5d8c0, 5, K.Concrete],
  ['plaster_3', 0xf2ead8, 5, K.Concrete],
  ['paint_white', 0xf4f4f4, 4, K.Plastic],
  ['paint_black', 0x26262b, 4, K.Plastic],
  ['paint_red', 0xd83a3a, 4, K.Plastic],
  ['paint_orange', 0xf08a2a, 4, K.Plastic],
  ['paint_yellow', 0xf5d23a, 4, K.Plastic],
  ['paint_lime', 0x9ad83a, 4, K.Plastic],
  ['paint_green', 0x3aa850, 4, K.Plastic],
  ['paint_teal', 0x2aa8a0, 4, K.Plastic],
  ['paint_cyan', 0x3ac8e8, 4, K.Plastic],
  ['paint_blue', 0x3a6ad8, 4, K.Plastic],
  ['paint_indigo', 0x4a3ab0, 4, K.Plastic],
  ['paint_purple', 0x8a3ac8, 4, K.Plastic],
  ['paint_magenta', 0xd83ab0, 4, K.Plastic],
  ['paint_pink', 0xf09ac0, 4, K.Plastic],
  ['paint_brown', 0x7a4a2a, 4, K.Plastic],
  ['paint_gray', 0x8a8a90, 4, K.Plastic],
  ['neon_red', 0xff4a4a, 4, K.Plastic, E],
  ['neon_orange', 0xff9a3a, 4, K.Plastic, E],
  ['neon_yellow', 0xffe84a, 4, K.Plastic, E],
  ['neon_green', 0x5aff6a, 4, K.Plastic, E],
  ['neon_cyan', 0x4af0ff, 4, K.Plastic, E],
  ['neon_blue', 0x4a7aff, 4, K.Plastic, E],
  ['neon_purple', 0xb44aff, 4, K.Plastic, E],
  ['neon_pink', 0xff5ac8, 4, K.Plastic, E],
  ['cloth_red', 0xc03a3a, 2, K.Cloth, F],
  ['cloth_blue', 0x3a5ac0, 2, K.Cloth, F],
  ['cloth_green', 0x3a9a4a, 2, K.Cloth, F],
  ['cloth_yellow', 0xe0c040, 2, K.Cloth, F],
  ['cloth_white', 0xe8e8e0, 2, K.Cloth, F],
  ['team_red', 0xd93a3a, 8, K.Plastic],
  ['team_blue', 0x3a7ad9, 8, K.Plastic],
  ['team_red_glow', 0xff5a5a, 8, K.Plastic, E],
  ['team_blue_glow', 0x5a9aff, 8, K.Plastic, E],
  ['core_red', 0xff6a5a, 20, K.Crystal, E | MatFlag.Core],
  ['core_blue', 0x5ab0ff, 20, K.Crystal, E | MatFlag.Core],
  ['crystal', 0x7af0ff, 8, K.Crystal, E],
  ['hay', 0xe0c050, 2, K.Leaves, F],
  ['obsidian', 0x2e2a36, 22, K.Stone],
  ['mossy_stone', 0x6f8a5a, 8, K.Stone],
  ['temple_1', 0x9a9070, 8, K.Stone],
  ['temple_2', 0x8a8062, 8, K.Stone],
  ['tile_white', 0xe8e8ee, 8, K.Concrete],
  ['tile_black', 0x2a2a30, 8, K.Concrete],
  ['carpet_red', 0x9a2a3a, 2, K.Cloth, F],
  ['scaffold', 0xe0b030, 15, K.Metal],
  ['built_brick', 0xc2603a, 8, K.Brick, MatFlag.Built],
  ['built_wood', 0xc49058, 4, K.Wood, MatFlag.Built | F],
  ['built_steel', 0x7a8594, 18, K.Metal, MatFlag.Built],
  ['water', 0x3a82c8, 0, K.Ice, T | MatFlag.Liquid],
  ['lava', 0xff6a1a, 0, K.Stone, E],
  ['mud', 0x5e4630, 16, K.Dirt, TR],
  ['clay', 0xb86a4a, 16, K.Dirt, TR],
  ['moon_rock_1', 0x9a98a0, 24, K.Stone, TR],
  ['moon_rock_2', 0x86848c, 24, K.Stone, TR],
  ['hull_white', 0xdde2ea, 18, K.Metal],
  ['hull_gray', 0xa6adb8, 18, K.Metal],
  ['pumpkin', 0xe07a1a, 2, K.Leaves, F],
  ['wheat', 0xe8d070, 1, K.Leaves, F],
  ['bone', 0xe8e0c8, 4, K.Stone],
  ['purple_goo', 0x9a3ad8, 2, K.Crystal, E],
  ['tnt', 0xd8342a, 2, K.Plastic, F | MatFlag.Explosive],
  ['rubber', 0x2a2a2a, 6, K.Plastic],
  ['chrome', 0xd8e0e8, 18, K.Metal],
  ['copper', 0xc07a4a, 15, K.Metal],
  ['jade', 0x3aa88a, 8, K.Stone],
  ['ruby', 0xd83a5a, 12, K.Crystal],
  ['coral', 0xf07a6a, 3, K.Stone],
  ['bamboo', 0x9ac85a, 3, K.Wood, F],
  // Opaque open sea (Voxel Island): players wade into it like water.
  ['deep_water', 0x3368c8, 0, K.Ice, MatFlag.Liquid],
  // Terrain-only rock under the soil (walls use stone_*/sandstone_*, which are softer).
  ['rock_1', 0x7f8287, 32, K.Stone, TR],
  ['rock_2', 0x8c8f94, 32, K.Stone, TR],
  ['rock_sand', 0xc9a26a, 24, K.Stone, TR],
];

/** Procedural surface pattern by material name. */
function patternFor(name: string): number {
  if (/^(brick_|built_brick)/.test(name)) return Pattern.Bricks;
  if (/^(planks|built_wood|bamboo)/.test(name)) return Pattern.Planks;
  if (/^(stone_|cobble_|sandstone_|temple_|mossy_stone|marble_|obsidian|moon_rock)/.test(name)) return Pattern.Blocks;
  if (/^(concrete_|asphalt)/.test(name)) return Pattern.Slabs;
  if (/^roof_/.test(name)) return Pattern.Tiles;
  if (/^log_/.test(name)) return Pattern.Bark;
  if (/^(metal_|steel_dark|plate|hull_|built_steel|scaffold|chrome|copper|rust)/.test(name)) return Pattern.Panels;
  if (/^tile_/.test(name)) return Pattern.Grid;
  return Pattern.None;
}

export const WORLD_MATERIALS: (MaterialDef | null)[] = [null];
for (const [name, color, hp, kind, flags] of defs) WORLD_MATERIALS.push({ name, color, hp, kind, flags, pattern: patternFor(name) });
if (WORLD_MATERIALS.length > 255) throw new Error('World palette overflow');

export const MATERIALS = new MaterialTable(WORLD_MATERIALS);
// Player movement (shared by server and client prediction) wades through liquids.
setLiquidMaterials(MATERIALS.flags);

/** Palette index by material name (throws on unknown names). */
export const M = (name: string): number => MATERIALS.id(name);

/** Numbered shade families for random selection: family('planks') = planks_1..planks_4. */
export function family(prefix: string): number[] {
  const out: number[] = [];
  const re = new RegExp(`^${prefix}(_\\d+)?$`);
  for (let i = 1; i < 255; i++) {
    if (re.test(MATERIALS.names[i])) out.push(i);
  }
  if (!out.length) throw new Error(`Empty material family ${prefix}`);
  return out;
}
